import { Suspense, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import { Outlet, useLocation } from 'react-router-dom';
import { BottomNav } from '@/components/organisms/BottomNav';
import { Sidebar } from '@/components/organisms/Sidebar';
import { Topbar } from '@/components/organisms/Topbar';
import { AIAssistant } from '@/components/organisms/AIAssistant';
import { CommandPalette } from '@/components/organisms/CommandPalette';
import { SkeletonLoader } from '@/components/atoms/SkeletonLoader';
import { PageTransition } from '@/components/PageTransition';
import { TopProgressBar } from '@/components/TopProgressBar';
import { usePermissions } from '@/hooks/usePermissions';
import { useRealtimeSync } from '@/hooks/useRealtime';
import { HotkeyProvider, useAppHotkeys } from '@/hooks/useAppHotkeys';
import { useTourAutoStart } from '@/hooks/useTourAutoStart';
import { bottomNavItems } from '@/navigation/sidebarNav';

/**
 * Authenticated application shell.
 *
 * `useRealtimeSync` is mounted here, once, rather than per page: the socket is
 * a single shared connection and its cache-invalidation rules must keep running
 * while the user navigates. Mounting it in pages would open and close the
 * connection on every route change.
 */
export function DashboardLayout(): JSX.Element {
  useRealtimeSync();
  useTourAutoStart();
  const { pathname } = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);

  return (
    <HotkeyProvider>
      <DashboardShell pathname={pathname} paletteOpen={paletteOpen} setPaletteOpen={setPaletteOpen} />
    </HotkeyProvider>
  );
}

function DashboardShell({
  pathname,
  paletteOpen,
  setPaletteOpen,
}: {
  pathname: string;
  paletteOpen: boolean;
  setPaletteOpen: (open: boolean) => void;
}): JSX.Element {
  useAppHotkeys({
    paletteOpen,
    onOpenPalette: () => setPaletteOpen(true),
    onClosePalette: () => setPaletteOpen(false),
  });

  const permissions = usePermissions();
  const hasBottomNav = useMemo(() => bottomNavItems(permissions).length > 0, [permissions]);

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: 'background.default',
        '--hub-bottom-chrome': hasBottomNav
          ? `calc(${theme.layout.bottomNavHeight}px + env(safe-area-inset-bottom, 0px))`
          : '0px',
        [theme.breakpoints.up('sm')]: {
          '--hub-bottom-chrome': '0px',
        },
      })}
    >
      <TopProgressBar />
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'absolute',
          left: -9999,
          top: 8,
          zIndex: (theme) => theme.zIndex.tooltip + 1,
          px: 4,
          py: 2,
          borderRadius: 1.5,
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          fontSize: '0.875rem',
          fontWeight: 600,
          '&:focus': { left: 8 },
        }}
      >
        Vai al contenuto principale
      </Box>

      <Sidebar />

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Topbar onOpenPalette={() => setPaletteOpen(true)} />

        <Box
          component="main"
          id="main-content"
          tabIndex={-1}
          sx={(theme) => ({
            flex: 1,
            minWidth: 0,
            overflowX: 'hidden',
            px: { xs: 4, md: 3 },
            pt: {
              xs: `calc(${theme.layout.topbarHeightMobile}px + env(safe-area-inset-top, 0px) + ${theme.spacing(4)})`,
              md: `calc(${theme.layout.topbarHeight}px + env(safe-area-inset-top, 0px) + ${theme.spacing(3)})`,
            },
            pb: {
              xs: hasBottomNav
                ? `calc(${theme.spacing(4)} + ${theme.layout.bottomNavHeight}px + env(safe-area-inset-bottom, 0px))`
                : 4,
              sm: 4,
              md: 3,
            },
            width: '100%',
            maxWidth: '100%',
            outline: 'none',
          })}
        >
          <Suspense
            key={pathname}
            fallback={
              <Box sx={{ position: 'relative' }}>
                <SkeletonLoader variant="card" count={2} height={120} />
              </Box>
            }
          >
            <PageTransition>
              <Outlet />
            </PageTransition>
          </Suspense>
        </Box>
      </Box>

      <BottomNav />
      <AIAssistant />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </Box>
  );
}
